/**
 * Which stand a container sits in — a student choice now, not a property of
 * the vessel shape. Real benches hold the same flask on a tripod, in a
 * retort clamp, or on a multi-level rod depending on what the experiment
 * needs, so the stand is its own apparatus with its own picker (see
 * ContainerContextMenu) rather than something derived from the glassware.
 *
 * Not every stand fits every vessel — a test tube in a tripod's gauze ring
 * would fall through it, a beaker in a wooden tube rack doesn't exist, and a
 * round-bottomed flask cannot stand on the bench at all — so each vessel
 * declares the stands it can actually use, the first being its default.
 * See docs/FeatureDesigns/chemistry-lab-target.md (Apparatus).
 */
import type { VesselType } from './vesselTypes';

export type StandType = 'none' | 'tripod' | 'retort' | 'tube-rack' | 'multi-level';

/** The catalogue the picker offers. 'none' is deliberately absent: it is the
 *  stored ABSENCE of a stand — a flat-bottomed vessel standing on the table,
 *  which is where a flask or beaker belongs until someone puts it somewhere —
 *  not a piece of apparatus you fetch from a cupboard. */
export const STAND_TYPES: StandType[] = ['tripod', 'retort', 'tube-rack', 'multi-level'];

export const STAND_LABELS: Record<StandType, string> = {
  none: 'On the Bench',
  tripod: 'Tripod & Gauze',
  retort: 'Retort Stand & Clamp',
  'tube-rack': 'Test-Tube Rack',
  'multi-level': 'Multi-Level Stand',
};

export const STAND_HINTS: Record<StandType, string> = {
  none: 'Standing flat on the bench, which is where flat-bottomed glassware sits until it needs to be held.',
  tripod: 'Tripod legs and a wire gauze — the burner goes underneath.',
  retort: 'A weighted base, an upright rod and a boss clamp holding the vessel.',
  'tube-rack': 'A wooden rack of wells. No flame near it — clamp the tube instead to heat it.',
  'multi-level': 'A tall rod. Put two neighbouring vessels on one and they share the rod, stacked.',
};

/** The stands each vessel can physically use, best/default first. */
export const STANDS_FOR_VESSEL: Record<VesselType, StandType[]> = {
  // Flat-bottomed: stands on the bench by default, hence 'none' first.
  flask: ['none', 'tripod', 'retort', 'multi-level'],
  beaker: ['none', 'tripod', 'retort', 'multi-level'],
  // Round-bottomed: cannot stand on the bench at all.
  'round-flask': ['tripod', 'retort', 'multi-level'],
  // A tube falls straight through a gauze ring, so no tripod — the rack is
  // the stand built for it.
  'test-tube': ['tube-rack', 'retort', 'multi-level'],
};

export function standsFor(vesselType: VesselType): StandType[] {
  return STANDS_FOR_VESSEL[vesselType];
}

export function defaultStandFor(vesselType: VesselType): StandType {
  return STANDS_FOR_VESSEL[vesselType][0];
}

export function isStandValidFor(vesselType: VesselType, stand: StandType): boolean {
  return STANDS_FOR_VESSEL[vesselType].includes(stand);
}

/** How many vessels one stand holds. Neighbouring containers on the same
 *  stand type share a stand up to this many (benchLayout groups them), and
 *  it's the same number the stand's right-click menu uses to decide whether
 *  there is room for another. */
export const STAND_CAPACITY: Record<StandType, number> = {
  none: 1,
  tripod: 1,
  retort: 1,
  'tube-rack': 4,
  'multi-level': 2,
};

/** The glassware a stand is built for. Tapping a stand that can't hold what's
 *  selected shouldn't be a dead end — it puts that stand on the bench with
 *  the vessel it DOES hold, which is how a real rack arrives: full of tubes. */
export const STAND_HOME_VESSEL: Record<StandType, VesselType> = {
  none: 'flask',
  tripod: 'flask',
  retort: 'flask',
  'tube-rack': 'test-tube',
  'multi-level': 'flask',
};

/** A wooden rack full of tubes is the one stand you never put a flame under —
 *  the same reason a real lab clamps a tube it intends to heat. */
export function standAllowsBurner(stand: StandType): boolean {
  return stand !== 'tube-rack';
}

/** Lighting a burner under a vessel standing flat on the bench would heat the
 *  bench. The burner brings its tripod with it, as it does in a real lab. */
export function standForBurner(stand: StandType): StandType {
  return stand === 'none' ? 'tripod' : stand;
}
